import React from 'react';

export const fmtKES = (n) =>
  `KES ${Number(n||0).toLocaleString('en-KE',{minimumFractionDigits:2})}`;
export const todayStr = () => new Date().toLocaleDateString('en-KE');

export function useBreakpoint() {
  const [bp, setBp] = React.useState(() =>
    typeof window !== 'undefined'
      ? window.innerWidth < 640 ? 'sm' : window.innerWidth < 1024 ? 'md' : 'lg'
      : 'lg'
  );
  React.useEffect(() => {
    const h = () => setBp(window.innerWidth < 640 ? 'sm' : window.innerWidth < 1024 ? 'md' : 'lg');
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return bp;
}

/* ── Toast ── */
export function Toast({ toast }) {
  if (!toast) return null;
  const isErr = toast.type === 'error';
  const isWarn = toast.type === 'warning';
  const accent = isErr ? '#ef4444' : isWarn ? '#f59e0b' : '#22c55e';
  const bg     = isErr ? 'rgba(239,68,68,0.08)' : isWarn ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)';
  const border = isErr ? 'rgba(239,68,68,0.25)' : isWarn ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.25)';
  const icon   = isErr ? '✕' : isWarn ? '⚠' : '✓';
  return (
    <div style={{
      position:'fixed', top:68, right:16, zIndex:9999,
      padding:'12px 16px 12px 14px', borderRadius:12, fontSize:13, fontWeight:600,
      fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif",
      background:'var(--bgCard)', color:'var(--textPrimary)',
      boxShadow:'0 8px 32px rgba(0,0,0,.22), 0 0 0 1px rgba(0,0,0,.08)',
      maxWidth:380, border:`1.5px solid ${border}`,
      display:'flex', alignItems:'flex-start', gap:10,
      animation:'toastIn 0.2s cubic-bezier(.16,1,.3,1)',
    }}>
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateX(12px)}to{opacity:1;transform:translateX(0)}}`}</style>
      <div style={{
        width:26, height:26, borderRadius:8, background:bg, border:`1.5px solid ${border}`,
        display:'flex', alignItems:'center', justifyContent:'center',
        flexShrink:0, color:accent, fontSize:12, fontWeight:800,
      }}>{icon}</div>
      <div style={{paddingTop:2, lineHeight:1.45}}>
        {isErr && <div style={{fontSize:10.5, textTransform:'uppercase', letterSpacing:'0.08em', color:accent, marginBottom:2, fontWeight:700}}>Error</div>}
        {isWarn && <div style={{fontSize:10.5, textTransform:'uppercase', letterSpacing:'0.08em', color:accent, marginBottom:2, fontWeight:700}}>Warning</div>}
        <span style={{color:'var(--textSecond)'}}>{toast.msg}</span>
      </div>
    </div>
  );
}

/* ── PageHeader — bold title like ServePoint ── */
export function PageHeader({ icon, title, sub, action, onBack }) {
  return (
    <div style={{ marginBottom:24 }} className="page-header">
      {onBack && (
        <button onClick={onBack} style={{
          display:'inline-flex',alignItems:'center',justifyContent:'center',
          width:32,height:32,borderRadius:8,
          border:'1.5px solid var(--border)',background:'var(--bgCard)',
          cursor:'pointer',marginBottom:12,color:'var(--textMuted)',
          transition:'all .15s',
        }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--gold)';e.currentTarget.style.color='var(--gold)';}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--textMuted)';}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
      )}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          {icon && <span style={{fontSize:22,opacity:.8}}>{icon}</span>}
          <div>
            <h1 style={{
              margin:0, fontSize:22, fontWeight:800,
              color:'var(--textPrimary)',
              fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif",
              lineHeight:1.2, letterSpacing:'-.3px',
            }}>{title}</h1>
            {sub && <p style={{margin:'3px 0 0',fontSize:12.5,color:'var(--textMuted)',fontWeight:400}}>{sub}</p>}
          </div>
        </div>
        {action && <div style={{flexShrink:0,display:'flex',gap:8,alignItems:'center'}}>{action}</div>}
      </div>
    </div>
  );
}

/* ── Stat Cards — ServePoint style: white cards, icon box, gold accent ── */
const STAT_PALETTES = [
  { icon_bg:'#1B3A2D', icon_color:'#C9A84C' },
  { icon_bg:'#C9A84C', icon_color:'#1B3A2D' },
  { icon_bg:'#0f4c81', icon_color:'#7dd3fc' },
  { icon_bg:'#166534', icon_color:'#86efac' },
  { icon_bg:'#7c2d12', icon_color:'#fdba74' },
  { icon_bg:'#4c1d95', icon_color:'#c4b5fd' },
  { icon_bg:'#155e75', icon_color:'#67e8f9' },
  { icon_bg:'#831843', icon_color:'#f9a8d4' },
];

export function StatCards({ stats, cards }) {
  const items = stats || cards || [];
  if (!items.length) return null;
  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14,marginBottom:24}}>
      {items.map((s, i) => {
        const p = STAT_PALETTES[i % STAT_PALETTES.length];
        const spark = s.spark || [];
        const ms = spark.length ? Math.max(...spark, 1) : 1;
        return (
          <div key={i} className="stat-tile" style={{position:'relative'}}>
            <div className="stat-label">
              {s.label}
              {s.icon && (
                <div style={{width:32,height:32,borderRadius:8,background:p.icon_bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,flexShrink:0,color:p.icon_color}}>
                  {s.icon}
                </div>
              )}
            </div>
            <div className="stat-value">{s.value}</div>
            {spark.length > 1 && (
              <svg viewBox={`0 0 ${spark.length*8} 28`} style={{width:'100%',height:28,display:'block',marginTop:10}} preserveAspectRatio="none">
                {(() => {
                  const pts = spark.map((v,j) => [j*8+4, 26-(v/ms)*22]);
                  return (<>
                    <path d={`M${pts[0][0]},28 L${pts.map(([x,y])=>`${x},${y}`).join(' L ')} L${pts[pts.length-1][0]},28 Z`} fill="rgba(201,168,76,0.12)"/>
                    <polyline points={pts.map(([x,y])=>`${x},${y}`).join(' ')} fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
                    <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="3" fill="#C9A84C"/>
                  </>);
                })()}
              </svg>
            )}
            <div className={`stat-trend ${s.trend > 0 ? 'stat-up' : s.trend < 0 ? 'stat-down' : 'stat-neutral'}`} style={{marginTop: spark.length>1?4:8}}>
              {s.sub && <span style={{fontSize:11,color:'var(--textFaint)',fontWeight:400,marginRight:'auto'}}>{s.sub}</span>}
              {s.trend != null && <span>{s.trend>0?'↑':s.trend<0?'↓':'→'} {Math.abs(s.trend)}%</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Card ── */
export function Card({ title, children, style = {}, action, noPad }) {
  return (
    <div className="gs-card" style={{marginBottom:16,...style}}>
      {(title||action) && (
        <div className="card-header">
          {title && <span className="card-title">{title}</span>}
          {action && <div style={{display:'flex',gap:6,alignItems:'center'}}>{action}</div>}
        </div>
      )}
      <div className={noPad?'':'card-body'}>{children}</div>
    </div>
  );
}

/* ── TabBar ── */
export function TabBar({ tabs, active, onChange }) {
  return (
    <div className="tab-bar-container tab-bar">
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} className={`tab-btn${active===t.id?' active':''}`}>
          {t.icon && <span style={{fontSize:13}}>{t.icon}</span>}
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function ResponsiveGrid({ cols=2, gap=14, children, style={} }) {
  return <div className={`r-grid r-grid-${cols}`} style={{gap,...style}}>{children}</div>;
}

/* ── Table ── */
export function Table({ headers, rows, mobileCard }) {
  const [w,setW] = React.useState(typeof window!=='undefined'?window.innerWidth:1200);
  React.useEffect(()=>{
    const h=()=>setW(window.innerWidth);
    window.addEventListener('resize',h);
    return ()=>window.removeEventListener('resize',h);
  },[]);
  if (w<640&&mobileCard) return <div style={{display:'grid',gap:8}}>{rows.map((row,i)=>mobileCard(row,i))}</div>;
  return (
    <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch',borderRadius:12,border:'1px solid var(--border)'}}>
      <table className="gs-table" style={{minWidth:320}}>
        <thead><tr>{headers.map(h=><th key={h}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((row,i)=><tr key={i}>{row.map((cell,j)=><td key={j}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

/* ── StatusPill ── */
export function StatusPill({ status }) {
  const MAP = {
    pending:{cls:'badge badge-amber',lbl:'Pending'},
    'payment-requested':{cls:'badge badge-red',lbl:'Awaiting Payment'},
    paid:{cls:'badge badge-green',lbl:'Paid'},
    preparing:{cls:'badge badge-blue',lbl:'Preparing'},
    ready:{cls:'badge badge-green',lbl:'Ready'},
    served:{cls:'badge badge-gray',lbl:'Served'},
    available:{cls:'badge badge-green',lbl:'Available'},
    occupied:{cls:'badge badge-blue',lbl:'Occupied'},
    maintenance:{cls:'badge badge-red',lbl:'Maintenance'},
    'checked-in':{cls:'badge badge-green',lbl:'Checked In'},
    'checked-out':{cls:'badge badge-gray',lbl:'Checked Out'},
    clean:{cls:'badge badge-green',lbl:'Clean'},
    dirty:{cls:'badge badge-amber',lbl:'Dirty'},
    inspecting:{cls:'badge badge-purple',lbl:'Inspecting'},
    'out-of-order':{cls:'badge badge-red',lbl:'Out of Order'},
    'in-progress':{cls:'badge badge-blue',lbl:'In Progress'},
    done:{cls:'badge badge-green',lbl:'Done'},
    approved:{cls:'badge badge-green',lbl:'Approved'},
    issue:{cls:'badge badge-red',lbl:'Issue'},
    confirmed:{cls:'badge badge-blue',lbl:'Confirmed'},
    cancelled:{cls:'badge badge-red',lbl:'Cancelled'},
    completed:{cls:'badge badge-green',lbl:'Completed'},
  };
  const item = MAP[status] || {cls:'badge badge-gray',lbl:status};
  return <span className={item.cls}>{item.lbl}</span>;
}

/* ── Field ── */
export function Field({ label, required, children, hint, error }) {
  return (
    <div style={{marginBottom:14}}>
      <label style={{display:'block',fontSize:12.5,color: error ? '#ef4444' : 'var(--textSecond)',marginBottom:5,fontWeight:600,fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif",transition:'color .15s'}}>
        {label}{required&&<span style={{color:'#ef4444',marginLeft:2}}>*</span>}
      </label>
      <div style={error ? {
        borderRadius:9, outline:'2px solid rgba(239,68,68,0.35)', outlineOffset:1,
      } : {}}>
        {children}
      </div>
      {error && (
        <div style={{display:'flex',alignItems:'center',gap:5,marginTop:5,animation:'fieldErrIn .15s ease'}}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5.5" stroke="#ef4444"/><path d="M6 3.5v3M6 8.5v.5" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round"/></svg>
          <span style={{fontSize:11.5,color:'#ef4444',fontWeight:600}}>{error}</span>
        </div>
      )}
      {!error && hint && <p style={{margin:'3px 0 0',fontSize:11.5,color:'var(--textFaint)'}}>{hint}</p>}
      <style>{`@keyframes fieldErrIn{from{opacity:0;transform:translateY(-3px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

/* ── Style presets ── */
export const inputStyle = {
  width:'100%', background:'var(--bgCard)', border:'1.5px solid var(--borderMid)',
  borderRadius:8, padding:'8px 12px', color:'var(--textPrimary)', fontSize:13,
  fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif",
  boxSizing:'border-box', outline:'none', transition:'border-color .15s, box-shadow .15s',
};

export const btnPrimary = {
  background:'#C9A84C', border:'none', borderRadius:8,
  padding:'9px 20px', color:'#1B3A2D', fontWeight:700, fontSize:13,
  cursor:'pointer', fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif",
  display:'inline-flex', alignItems:'center', gap:7, transition:'all .15s',
  boxShadow:'0 2px 10px rgba(201,168,76,0.3)',
};

export const btnSm = {
  background:'var(--bgCard)', border:'1.5px solid var(--borderMid)', borderRadius:7,
  padding:'5px 11px', color:'var(--textSecond)', fontSize:12.5, cursor:'pointer',
  fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif",
  whiteSpace:'nowrap', transition:'all .15s', fontWeight:500,
};

/* ── Empty / Loading ── */
export function Empty({ text }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">—</div>
      <div className="empty-state-text">{text||'No records found'}</div>
    </div>
  );
}

export function Loading() {
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'48px 0',gap:10,color:'var(--textMuted)',fontSize:13}}>
      <div className="spinner"/>Loading…
    </div>
  );
}

export function ProgressBar({ pct, color='#C9A84C' }) {
  return (
    <div style={{height:6,background:'var(--border)',borderRadius:99,overflow:'hidden',width:'100%'}}>
      <div style={{height:'100%',width:`${Math.min(100,pct)}%`,background:pct>=100?'#22c55e':color,transition:'width .4s cubic-bezier(.16,1,.3,1)',borderRadius:99}}/>
    </div>
  );
}

export function NotificationBell({ count, onClick }) {
  return (
    <button onClick={onClick} style={{position:'relative',background:'transparent',border:'1.5px solid var(--border)',borderRadius:8,cursor:'pointer',padding:'7px 9px',color:'var(--textMuted)',display:'flex',alignItems:'center',transition:'all .15s'}}
      onMouseEnter={e=>{e.currentTarget.style.background='var(--bgHover)';e.currentTarget.style.borderColor='var(--gold)';e.currentTarget.style.color='var(--gold)';}}
      onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--textMuted)';}}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
      </svg>
      {count>0&&<span style={{position:'absolute',top:2,right:2,background:'#C9A84C',color:'#1B3A2D',borderRadius:'50%',fontSize:8,fontWeight:800,width:14,height:14,display:'flex',alignItems:'center',justifyContent:'center'}}>{count>9?'9+':count}</span>}
    </button>
  );
}

export function CheckItem({ label, checked, onChange, disabled }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:9,padding:'9px 12px',cursor:disabled?'default':'pointer',borderBottom:'1px solid var(--border)',background:checked?'rgba(201,168,76,0.05)':'transparent',transition:'background .12s',borderRadius:8}}>
      <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${checked?'#C9A84C':'var(--borderMid)'}`,background:checked?'#C9A84C':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all .15s'}}>
        {checked&&<svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#1B3A2D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      </div>
      <span style={{fontSize:13,color:checked?'#C9A84C':'var(--textSecond)',textDecoration:checked?'line-through':'none',fontWeight:500}}>{label}</span>
      {!disabled&&<input type="checkbox" checked={checked} onChange={onChange} style={{display:'none'}}/>}
    </label>
  );
}

export function SparkBars({ data, color='#C9A84C', height=32 }) {
  if (!data||!data.length) return null;
  const max=Math.max(...data.map(d=>d.value!==undefined?d.value:d),1);
  return (
    <div style={{display:'flex',alignItems:'flex-end',gap:2,height,width:'100%'}}>
      {data.map((d,i)=>{
        const v=d.value!==undefined?d.value:d;
        return <div key={i} title={d.label||''} style={{flex:1,height:Math.max(2,(v/max)*height),background:color,borderRadius:'3px 3px 0 0',opacity:.3+(i/data.length)*.7}}/>;
      })}
    </div>
  );
}
